import React from 'react';
import ContainerHeader from '../components/ContainerHeader';
import {
    Button, Tab, Tabs, Chip, Typography, Box, IconButton, TextField, InputAdornment,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
    Paper, Popover, Checkbox, FormControlLabel, CircularProgress,
} from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import ViewListIcon from '@material-ui/icons/ViewList';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import useHelpDesk, { LIST_COLUMNS, getPriority, stripHtml, formatDate } from './useHelpDesk';
import AddEditHelpDeskTaskModal from './Components/AddHelpDeskTaskModal';


// Warm palette, kept consistent with the rest of the page's design tokens.
const COLORS = {
    bg: '#fbf7f1',
    surface: '#ffffff',
    border: '#cee2d6',
    text: '#362b21',
    textSoft: '#1c211f',
    accent: '#0C9344',
    accentSoft: '#cbebd9',
    danger: '#a83e32',
    dangerSoft: '#f6e4e1',
};

const useStyles = makeStyles(() => ({
    root: {
        margin: '0 auto',
    },
    headCellInner: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
    },
    filterIconBtn: {
        padding: 3,
        color: COLORS.textSoft,
        opacity: 0.55,
        '&:hover': { opacity: 1 },
    },
    filterIconActive: {
        opacity: 1,
        color: COLORS.accent,
        backgroundColor: COLORS.accentSoft,
    },
    popoverPaper: {
        width: 260,
        padding: 12,
        borderRadius: 8,
        border: `1px solid ${COLORS.border}`,
    },
    popoverTitle: {
        fontSize: '0.82rem',
        fontWeight: 700,
        color: COLORS.text,
        marginBottom: 8,
    },
    popoverSearch: {
        width: '100%',
        '& .MuiInputBase-input': { fontSize: '0.8rem', padding: '7px 8px' },
        '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: COLORS.border },
            '&:hover fieldset, &.Mui-focused fieldset': { borderColor: COLORS.accent },
        },
    },
    popoverQuickActions: {
        display: 'flex',
        justifyContent: 'space-between',
        margin: '6px 0 2px',
    },
    optionList: {
        maxHeight: 220,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        borderTop: `1px solid ${COLORS.border}`,
        borderBottom: `1px solid ${COLORS.border}`,
        padding: '4px 0',
    },
    optionItem: {
        margin: 0,
        '& .MuiFormControlLabel-label': {
            fontSize: '0.8rem',
            color: COLORS.text,
            overflowWrap: 'anywhere',
        },
    },
    popoverActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 8,
        marginTop: 10,
    },
    headerRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        marginBottom: 4,
    },
    headerRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
    },
    searchField: {
        minWidth: 260,
        backgroundColor: COLORS.surface,
        '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': { borderColor: COLORS.border },
            '&:hover fieldset': { borderColor: COLORS.accent },
            '&.Mui-focused fieldset': { borderColor: COLORS.accent },
        },
        '& .MuiInputBase-input': {
            fontSize: '0.85rem',
        },
    },
    newTaskBtn: {
        backgroundColor: COLORS.accent,
        color: '#fff',
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        '&:hover': { backgroundColor: '#096e33' },
    },
    banner: {
        backgroundColor: COLORS.dangerSoft,
        color: COLORS.danger,
        border: `1px solid #bae6c2`,
        borderRadius: 8,
        padding: '10px 14px',
        fontSize: 13.5,
        marginBottom: 16,
    },
    tabsRoot: {
        borderBottom: `1px solid ${COLORS.border}`,
        marginBottom: 8,
        minHeight: 44,
        '& .MuiTabs-indicator': { backgroundColor: COLORS.accent, height: 2 },
    },
    tab: {
        minWidth: 'auto',
        minHeight: 44,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.85rem',
        color: COLORS.textSoft,
        padding: '6px 14px',
        '&.Mui-selected': { color: COLORS.text },
    },
    badge: {
        marginLeft: 6,
        height: 18,
        fontSize: '0.68rem',
        fontWeight: 700,
        backgroundColor: COLORS.accentSoft,
        color: COLORS.accent,
    },

    emptyState: {
        padding: '56px 0',
        textAlign: 'center',
        color: COLORS.textSoft,
    },
    row: {
        borderBottom: `1px solid ${COLORS.border}`,
        backgroundColor: COLORS.surface,
    },
    rowMain: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 4px',
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#f0f0f0' },
    },
    priorityBar: {
        width: 4,
        alignSelf: 'stretch',
        minHeight: 22,
        borderRadius: 2,
        flexShrink: 0,
    },
    rowName: {
        flex: 1,
        minWidth: 0,
        fontWeight: 500,
        fontSize: '0.92rem',
        color: COLORS.text,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    rowMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexShrink: 0,
    },
    priorityChip: {
        height: 22,
        fontSize: '0.72rem',
        fontWeight: 700,
        backgroundColor: 'transparent',
        border: '1px solid currentColor',
    },
    statusChip: {
        height: 22,
        fontSize: '0.72rem',
        fontWeight: 600,
        color: COLORS.textSoft,
        border: `1px solid ${COLORS.border}`,
        backgroundColor: 'transparent',
    },
    dateText: {
        fontSize: '0.75rem',
        color: COLORS.textSoft,
        whiteSpace: 'nowrap',
    },
    expandIcon: {
        padding: 4,
        color: COLORS.textSoft,
        transition: 'transform 0.15s ease',
    },
    expandIconOpen: {
        transform: 'rotate(180deg)',
    },
    rowDetail: {
        padding: '0 4px 18px 22px',
    },
    description: {
        fontSize: '0.85rem',
        color: COLORS.text,
        lineHeight: 1.55,
        marginBottom: 8,
    },
    rowFooter: {
        display: 'flex',
        gap: 16,
        fontSize: '0.72rem',
        color: COLORS.textSoft,
    },
    viewToggle: {
        display: 'flex',
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        backgroundColor: COLORS.surface,
    },
    viewToggleButton: {
        padding: 7,
        borderRadius: 6,
        color: COLORS.textSoft,
    },
    viewToggleButtonActive: {
        backgroundColor: COLORS.accentSoft,
        color: COLORS.accent,
    },
    gridBoard: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))',
        alignItems: 'start',
        gap: 18,
        paddingTop: 16,
    },
    gridCategory: {
        minWidth: 0,
    },
    gridCategoryHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        borderBottom: `2px solid ${COLORS.border}`,
        padding: '0 0 8px',
        marginBottom: 10,
    },
    gridCategoryTitle: {
        fontSize: '0.9rem',
        fontWeight: 700,
        color: COLORS.text,
    },
    ticketGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: 9,
    },
    ticketCard: {
        minWidth: 0,
        border: `1px solid ${COLORS.border}`,
        borderLeft: `4px solid ${COLORS.accent}`,
        borderRadius: 6,
        backgroundColor: COLORS.surface,
        padding: 12,
    },
    ticketHeading: {
        display: 'flex',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        columnGap: 7,
        rowGap: 2,
        marginBottom: 6,
    },
    ticketId: {
        fontSize: '0.7rem',
        fontWeight: 700,
        color: COLORS.accent,
        whiteSpace: 'nowrap',
    },
    ticketTitle: {
        fontSize: '0.88rem',
        fontWeight: 700,
        color: COLORS.text,
        overflowWrap: 'anywhere',
    },
    ticketDescription: {
        fontSize: '0.78rem',
        color: COLORS.textSoft,
        lineHeight: 1.45,
        marginBottom: 10,
        overflowWrap: 'anywhere',
    },
    ticketMeta: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '6px 12px',
        fontSize: '0.72rem',
        color: COLORS.textSoft,
    },
    ticketCardInteractive: {
        cursor: 'pointer',
        '&:hover': { borderColor: COLORS.accent, backgroundColor: '#f8fcf9' },
        '&:focus': { outline: `2px solid ${COLORS.accent}`, outlineOffset: 2 },
    },
    ticketPriority: {
        height: 20,
        fontSize: '0.68rem',
        fontWeight: 700,
    },
    dialogDescription: {
        fontSize: '0.88rem',
        lineHeight: 1.6,
        color: COLORS.text,
        marginBottom: 18,
        overflowWrap: 'anywhere',
    },
    dialogMeta: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 12,
        paddingTop: 14,
        borderTop: `1px solid ${COLORS.border}`,
        fontSize: '0.82rem',
        color: COLORS.textSoft,
    },
    tableContainer: {
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        marginTop: 8,
        backgroundColor: COLORS.surface,
    },
    headCell: {
        color: COLORS.text,
        fontWeight: 700,
        fontSize: '0.8rem',
        whiteSpace: 'nowrap',
        borderBottom: 'none',
        paddingBottom: 4,
    },
    filterCell: {
        padding: '0 12px 10px',
        borderBottom: `1px solid ${COLORS.border}`,
    },
    filterInput: {
        minWidth: 90,
        width: '100%',
        '& .MuiInputBase-input': { fontSize: '0.78rem', padding: '6px 8px' },
        '& .MuiOutlinedInput-root': {
            backgroundColor: COLORS.surface,
            '& fieldset': { borderColor: COLORS.border },
            '&:hover fieldset, &.Mui-focused fieldset': { borderColor: COLORS.accent },
        },
    },
    bodyRow: {
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#f8fcf9' },
    },
    bodyCell: {
        fontSize: '0.85rem',
        color: COLORS.text,
        borderBottom: `1px solid ${COLORS.border}`,
    },
    detailCell: {
        padding: '0 16px 16px 32px',
        backgroundColor: COLORS.bg,
    },
    dialogTitleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
    },
    dialogTitleText: {
        flex: 1,
        minWidth: 0,
        overflowWrap: 'anywhere',
    },
    openExternalBtn: {
        flexShrink: 0,
        color: COLORS.accent,
        '&:hover': { backgroundColor: COLORS.accentSoft },
    },
    linkError: {
    color: COLORS.danger,
    backgroundColor: COLORS.dangerSoft,
    borderRadius: 6,
    padding: '8px 12px',
    fontSize: '0.8rem',
    marginBottom: 12,
},
}));

/**
 * @param {Object} props
 * @param {number} props.userId - current user id, sent when a task is created
 * @param {number} props.projectId - current project id, sent when a task is created
 * @param {Object} [props.match] - react-router match, passed straight through to ContainerHeader
 */

function HelpDesk(props) {
    const classes = useStyles();
    const { userId, projectId } = props;

    const {
        statuses, displayedTasks, gridGroups, listTasks, displayCounts, totalBadgeCount,
        isLoading, isSubmitting, error,
        searchQuery, setSearchQuery, isSearching, viewMode, setViewMode,
        activeTabIndex, handleTabChange,
        sortState, handleSort, columnFilters, hasColumnFilters, clearColumnFilters,
        filterPopover, activeFilterColumn, visibleOptions, draftSelection,
        optionSearch, setOptionSearch,
        openFilterPopover, closeFilterPopover, toggleDraftOption,
        selectAllVisible, clearDraftSelection, applyFilter,
        isModalOpen, openCreateModal, closeCreateModal, handleSubmitTask,
        selectedGridTask, openTask, handleCloseModal, handleOpenExternal,
        isOpeningTicket, linkError,
    } = useHelpDesk({ userId, projectId });

    return (
        <div className={`app-wrapper siteWrapper ${classes.root}`}>
            <Box className={classes.headerRow}>
                <ContainerHeader match={props.match} title="Help Desk" />
                <div className={classes.headerRight}>
                    <div className={classes.viewToggle} role="group" aria-label="Ticket view">
                        <Tooltip title="List view">
                            <IconButton
                                size="small"
                                aria-label="List view"
                                aria-pressed={viewMode === 'list'}
                                className={`${classes.viewToggleButton} ${viewMode === 'list' ? classes.viewToggleButtonActive : ''}`}
                                onClick={() => setViewMode('list')}
                            >
                                <ViewListIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Grid view">
                            <IconButton
                                size="small"
                                aria-label="Grid view"
                                aria-pressed={viewMode === 'grid'}
                                className={`${classes.viewToggleButton} ${viewMode === 'grid' ? classes.viewToggleButtonActive : ''}`}
                                onClick={() => setViewMode('grid')}
                            >
                                <ViewModuleIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </div>
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Search tasks…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={classes.searchField}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" style={{ color: COLORS.textSoft }} />
                                </InputAdornment>
                            ),
                            endAdornment: isSearching ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearchQuery('')} aria-label="Clear search">
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    <Button
                        variant="contained"
                        className={classes.newTaskBtn}
                        startIcon={<AddIcon />}
                        onClick={openCreateModal}
                    >
                        New task
                    </Button>
                </div>
            </Box>

            {error && <div className={classes.banner}>{error}</div>}

            <Tabs value={activeTabIndex} onChange={handleTabChange} className={classes.tabsRoot}>
                <Tab
                    className={classes.tab}
                    label={
                        <span>
                            All
                            {totalBadgeCount > 0 && (
                                <Chip label={totalBadgeCount} size="small" className={classes.badge} />
                            )}
                        </span>
                    }
                />
                {statuses.map((status) => (
                    <Tab
                        key={status.id}
                        className={classes.tab}
                        label={
                            <span>
                                {status.statusName}
                                {displayCounts[status.id] > 0 && (
                                    <Chip label={displayCounts[status.id]} size="small" className={classes.badge} />
                                )}
                            </span>
                        }
                    />
                ))}
            </Tabs>

            <div className={classes.list}>
                {isLoading && (
                    <Typography className={classes.emptyState} variant="body2">
                        Loading tickets…
                    </Typography>
                )}

                {!isLoading && displayedTasks.length === 0 && (
                    <Box className={classes.emptyState}>
                        <Typography variant="body2" gutterBottom>
                            {isSearching ? 'No tickets match your search.' : 'No tickets here yet.'}
                        </Typography>
                        {!isSearching && (
                            <Button className={classes.newTaskBtn} onClick={openCreateModal} style={{ marginTop: 8 }}>
                                Create the first one
                            </Button>
                        )}
                    </Box>
                )}

                {!isLoading && viewMode === 'grid' && (
                    <div className={classes.gridBoard}>
                        {gridGroups.map((group) => (
                            <section key={group.id} className={classes.gridCategory}>
                                <div className={classes.gridCategoryHeader}>
                                    <Typography className={classes.gridCategoryTitle}>{group.name}</Typography>
                                    <Chip label={group.tasks.length} size="small" className={classes.badge} />
                                </div>
                                <div className={classes.ticketGrid}>
                                    {group.tasks.map((task) => {
                                        const priority = getPriority(task.priorityId);
                                        return (
                                            <article
                                                key={task.tId}
                                                className={`${classes.ticketCard} ${classes.ticketCardInteractive}`}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => openTask(task)}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault();
                                                        openTask(task);
                                                    }
                                                }}
                                            >
                                                <div className={classes.ticketHeading}>
                                                    <span className={classes.ticketId}>Ticket #{task.tRefNumber || task.tId}</span>
                                                    <Typography className={classes.ticketTitle} component="span">
                                                        {task.taskName}
                                                    </Typography>
                                                </div>
                                                <Typography className={classes.ticketDescription}>
                                                    {task.taskDescription
                                                        ? stripHtml(task.taskDescription)
                                                        : 'No description provided.'}
                                                </Typography>
                                                <div className={classes.ticketMeta}>
                                                    <Chip
                                                        label={priority.name}
                                                        size="small"
                                                        className={classes.ticketPriority}
                                                        style={{ color: priority.color, backgroundColor: 'transparent', border: `1px solid ${priority.color}` }}
                                                    />
                                                    <span>Status: {task.statusDescription || group.name}</span>
                                                    <span>Due: {formatDate(task.tDueDate)}</span>
                                                    <span>Reported by: {task.reportedByName || 'Unknown'}</span>
                                                    <span>Created: {formatDate(task.dateCreated)}</span>
                                                    {task.isCompleted && (
                                                        <span>Completed: {formatDate(task.dateCompleted)}</span>
                                                    )}
                                                </div>
                                            </article>
                                        );
                                    })}
                                    {group.tasks.length === 0 && (
                                        <Typography variant="body2" color="textSecondary">
                                            No tickets
                                        </Typography>
                                    )}
                                </div>
                            </section>
                        ))}
                    </div>
                )}

                {!isLoading && viewMode === 'list' && displayedTasks.length > 0 && (
                    <TableContainer component={Paper} elevation={0} className={classes.tableContainer}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {LIST_COLUMNS.map((col) => {
                                        const isFiltered = (columnFilters[col.id] || []).length > 0;
                                        return (
                                            <TableCell
                                                key={col.id}
                                                className={classes.headCell}
                                                sortDirection={sortState.by === col.id ? sortState.dir : false}
                                            >
                                                <div className={classes.headCellInner}>
                                                    <TableSortLabel
                                                        active={sortState.by === col.id}
                                                        direction={sortState.by === col.id ? sortState.dir : 'asc'}
                                                        onClick={() => handleSort(col.id)}
                                                    >
                                                        {col.label}
                                                    </TableSortLabel>
                                                    <Tooltip title={isFiltered ? `Filtered (${columnFilters[col.id].length})` : 'Filter'}>
                                                        <IconButton
                                                            size="small"
                                                            aria-label={`Filter ${col.label}`}
                                                            className={`${classes.filterIconBtn} ${isFiltered ? classes.filterIconActive : ''}`}
                                                            onClick={(e) => openFilterPopover(e, col.id)}
                                                        >
                                                            <FilterListIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </div>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {listTasks.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={LIST_COLUMNS.length} align="center" className={classes.bodyCell}>
                                            No tickets match the column filters.{' '}
                                            {hasColumnFilters && (
                                                <Button size="small" onClick={clearColumnFilters}>
                                                    Clear filters
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )}

                                {listTasks.map((task) => {
                                    const priority = getPriority(task.priorityId);
                                    return (
                                        <TableRow
                                            key={task.tId}
                                            hover
                                            className={classes.bodyRow}
                                            tabIndex={0}
                                            onClick={() => openTask(task)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    openTask(task);
                                                }
                                            }}
                                        >
                                            <TableCell className={classes.bodyCell}>#{task.tRefNumber || task.tId}</TableCell>
                                            <TableCell className={classes.bodyCell} style={{ fontWeight: 500 }}>
                                                {task.taskName}
                                            </TableCell>
                                            <TableCell className={classes.bodyCell}>
                                                <Chip
                                                    label={priority.name}
                                                    size="small"
                                                    className={classes.priorityChip}
                                                    style={{ color: priority.color }}
                                                />
                                            </TableCell>
                                            <TableCell className={classes.bodyCell}>
                                                <Chip label={task.statusDescription} size="small" className={classes.statusChip} />
                                            </TableCell>
                                            <TableCell className={classes.bodyCell}>{formatDate(task.tDueDate)}</TableCell>
                                            <TableCell className={classes.bodyCell}>{task.reportedByName || 'Unknown'}</TableCell>
                                            <TableCell className={classes.bodyCell}>{formatDate(task.dateCreated)}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </div>

            <Popover
                open={Boolean(filterPopover.anchorEl)}
                anchorEl={filterPopover.anchorEl}
                onClose={closeFilterPopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                classes={{ paper: classes.popoverPaper }}
            >
                {activeFilterColumn && (
                    <>
                        <Typography className={classes.popoverTitle}>
                            Filter by {activeFilterColumn.label}
                        </Typography>

                        <TextField
                            variant="outlined"
                            size="small"
                            placeholder="Search options…"
                            className={classes.popoverSearch}
                            value={optionSearch}
                            onChange={(e) => setOptionSearch(e.target.value)}
                            autoFocus
                        />

                        <div className={classes.popoverQuickActions}>
                            <Button size="small" onClick={selectAllVisible}>Select all</Button>
                            <Button size="small" onClick={clearDraftSelection}>Clear</Button>
                        </div>

                        <div className={classes.optionList}>
                            {visibleOptions.length === 0 && (
                                <Typography variant="body2" color="textSecondary" style={{ padding: 8 }}>
                                    No options found
                                </Typography>
                            )}
                            {visibleOptions.map((opt) => (
                                <FormControlLabel
                                    key={opt}
                                    className={classes.optionItem}
                                    control={
                                        <Checkbox
                                            size="small"
                                            color="primary"
                                            checked={draftSelection.includes(opt)}
                                            onChange={() => toggleDraftOption(opt)}
                                        />
                                    }
                                    label={opt}
                                />
                            ))}
                        </div>

                        <div className={classes.popoverActions}>
                            <Button size="small" onClick={closeFilterPopover}>Cancel</Button>
                            <Button
                                size="small"
                                variant="contained"
                                className={classes.newTaskBtn}
                                onClick={applyFilter}
                            >
                                Apply
                            </Button>
                        </div>
                    </>
                )}
            </Popover>

            <Dialog
                open={Boolean(selectedGridTask)}
                onClose={handleCloseModal}
                maxWidth="sm"
                fullWidth
            >
                {selectedGridTask && (
                    <>
                        <DialogTitle disableTypography>
                            <div className={classes.dialogTitleRow}>
                                <Typography variant="h6" className={classes.dialogTitleText}>
                                    Ticket #{selectedGridTask.tRefNumber || selectedGridTask.tId} - {selectedGridTask.taskName}
                                </Typography>
                                <Tooltip title="Open in Help Desk (new tab)">
                                    <span>
                                        <IconButton
                                            aria-label="Open ticket in Help Desk in a new tab"
                                            className={classes.openExternalBtn}
                                            onClick={() => handleOpenExternal(selectedGridTask?.tId)}
                                            disabled={isOpeningTicket}
                                        >
                                            {isOpeningTicket ? (
                                                <CircularProgress size={18} />
                                            ) : (
                                                <OpenInNewIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </div>
                        </DialogTitle>
                        <DialogContent dividers>
                            {linkError && <div className={classes.linkError}>{linkError}</div>}
                            <Typography className={classes.dialogDescription} component="div">
                                {selectedGridTask.taskDescription
                                    ? <div dangerouslySetInnerHTML={{ __html: selectedGridTask.taskDescription }} />
                                    : 'No description provided.'}
                            </Typography>
                            <div className={classes.dialogMeta}>
                                <span>Status: {selectedGridTask.statusDescription || '—'}</span>
                                <span>Priority: {getPriority(selectedGridTask.priorityId).name}</span>
                                <span>Due: {formatDate(selectedGridTask.tDueDate)}</span>
                                <span>Reported by: {selectedGridTask.reportedByName || 'Unknown'}</span>
                                <span>Created: {formatDate(selectedGridTask.dateCreated)}</span>
                                {selectedGridTask.isCompleted && (
                                    <span>Completed: {formatDate(selectedGridTask.dateCompleted)}</span>
                                )}
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseModal} color="primary">
                                Close
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <AddEditHelpDeskTaskModal
                open={isModalOpen}
                onClose={closeCreateModal}
                statuses={statuses}
                isSubmitting={isSubmitting}
                onSubmit={handleSubmitTask}
            />
        </div>
    );
}

export default HelpDesk;